import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import CardDetail from '#client/components/MoveMoney/components/SummaryFormComponents/CardDetail';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import { convertCentToUSDText } from '#client/components/MoveMoney/utils/common';
import { FAQ_OVERVIEW_URL } from '#client/constants/FAQs';
import useFormStep from '#client/hooks/useFormStep';
import { capitalizeFirstLetter, formatAccountNumber } from '#client/utils/text';

import { DialogContentStyled, Wrapper } from './ErrorForm.styles';

import { MODAL_ACTION } from '../constants/instants';

const failedIcon = (
  <SharedAsset
    alt=""
    height={50}
    src="/icon/micro/x.svg"
    width={50}
  />
);

export default function ErrorForm({ errorSubTitle, errorDetail }) {
  const { depositData, dispatchDepositData } = useFormStep();
  const { selectedFromAccount, amount, initialDate, toAccount } = depositData;
  const { cardHolderFirstName, cardHolderLastName, cardLastFourDigits, cardType } =
    selectedFromAccount;
  const fromCardHolder = [cardHolderFirstName, cardHolderLastName].join(' ');
  const fromType = capitalizeFirstLetter(cardType);
  const fromCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits,
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const amountInUSD = convertCentToUSDText(amount);
  const toName = toAccount?.name ?? '';

  const handleTryAgain = () => {
    dispatchDepositData({
      type: MODAL_ACTION.TRY_AGAIN
    });
  };

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper role="form">
              <Stack
                alignItems="center"
                flexDirection={{ xs: 'column', lg: 'row' }}
                gap="8px"
                justifyContent="center"
                paddingBottom={{ xs: '32px', lg: '40px' }}
              >
                {failedIcon}
                <Heading
                  align="center"
                  size="3xl"
                  weight="bold"
                >
                  Deposit failed
                </Heading>
              </Stack>
              <Paper variant="outlined">
                <TransactionDetail
                  amount={amountInUSD}
                  dateInitiated={initialDate}
                  status="Failed"
                  subTitle={errorDetail}
                  title={errorSubTitle}
                  transferType="Instant deposit"
                />
                <Divider />
                <RecipientDetail
                  accountHolder={toName}
                  accountNumber={toAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={toAccount?.accountNumber}
                  title="Recipient"
                />
                <Divider />
                <CardDetail
                  cardHolder={fromCardHolder}
                  cardNumber={fromCardNumber}
                  type={fromType}
                />
              </Paper>
              <Box mt={{ xs: '32px', lg: '40px' }}>
                <Text
                  color="tertiary"
                  size="sm"
                >
                  Need more help?{' '}
                  <Link
                    href={FAQ_OVERVIEW_URL}
                    target="_blank"
                    underline="always"
                  >
                    FAQs
                  </Link>
                </Text>
              </Box>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          fullWidth
          onClick={handleTryAgain}
          size="large"
          variant="contained"
        >
          Try again
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

ErrorForm.propTypes = {
  errorSubTitle: PropTypes.string.isRequired,
  errorDetail: PropTypes.string.isRequired
};
